import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import { UserType, DIReason } from "shared/cubiq-model-multi-asset";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  VisibilityModel,
  ACTIVE_ORDER_STATES,
  MONITORED_ORDER_STATES,
  BENCHMARK_REJECT_STATES,
  HEDGE_ORDER_REJECT_STATES,
  ALGO_MONITORED_ORDER_STATES,
  checkMonitoredOrderState,
} from "./VisibilityModel";

export class ActiveUser {
  // Basic info
  public userId: string;
  public username: string;
  public userCode: string;
  public clientCode: string;
  // USER Type
  public userTypeId: UserType;
  public userTypeName: string; // 0=CLIENT, 1=SALES, 2=TRADER, 3=ADMIN, 4=CLIENT_TRADER
  // Sales Group
  public salesGroupsIdSet: Set<string> = new Set();
  public salesGroupsNameSet: Set<string> = new Set();
  // Trading Groups
  public tradingGroupsIdSet: Set<string> = new Set();
  public tradingGroupsNameSet: Set<string> = new Set();
  // User branch code
  public branchCd: string;
  // User Time zone
  public timeZoneCd: string;
  // Clients that are visible
  public clientIdSet: Set<string> = new Set();
  public clientNameSet: Set<string> = new Set();
  // Clients that in my sales group
  public clientIdsInSalesGroupSet: Set<string> = new Set();
  // Default Position book
  public defaultPositionBookId: string;
  public defaultPositionBookName: string;
  // Authentication provider ID
  public authProvider: string;
  // html profile id
  public defaultPublicProfileId: CubiqTS.Long;
  // User Notification settings
  public emailAddress: string;
  public smsNumber: string;
  public sendEmailAnyOrderIFillFg: boolean;
  public sendEmailMyOrdEnteredFg: boolean;
  public sendEmailMyOrdExpiryFg: boolean;
  public sendEmailMyOrdFillFg: boolean;
  public sendSMSAnyOrderIFillFg: boolean;
  public sendSMSMyOrdEnteredFg: boolean;
  public sendSMSMyOrdExpiryFg: boolean;
  public sendSMSMyOrdFillFg: boolean;
  public sendSMSMyOrdModifiedFg: boolean;
  public sendEmailMyOrdModifiedFg: boolean;
  public sendEmailMyOrdCanceledFg: boolean;
  public sendSMSMyOrdCanceledFg: boolean;

  /**********************************************/
  /* NOT CURRENTLY IN USE  */
  // Roles/Permissions see systemConfigPrimaryService

  // Profile settings
  public blotterFilters: Map<string, number> = new Map(); // <filterName, DataProfileType>
  public profileSettings: Map<string, string> = new Map(); // <key, value>
  // no idea what updateMask is :)

  public entryUnRestricted: boolean = false;
  /**
   * Does the user have visibility unlimited role
   */
  public ordersUnlimited: boolean = false;
  public visibilityPrivateLimitOrders: boolean = false;
  public visibilityPrivateBMOrders: boolean = false;
  public preVisibilityVisible: boolean = false;

  public visibilityModel: VisibilityModel;

  //
  public tradableBranchCodes: Set<string> = new Set();
  public tradableClientCodes: Set<string> = new Set();
  public tradableAccountCodes: Set<string> = new Set();
  public requestOrderTypes: Set<CubiqTS.PlRequestOrderTypeStrategy> = new Set();

  public tradableAlgos: Set<string> = new Set();

  public isAdmin(): boolean {
    return this.userTypeId == UserType.ADMIN;
  }

  public isTrader(): boolean {
    return this.userTypeId == UserType.TRADER;
  }

  public isSales(): boolean {
    return this.userTypeId == UserType.SALES;
  }

  public isClient(): boolean {
    return this.userTypeId == UserType.CLIENT;
  }

  public canSeeIntervention(
    entryUserCode: string,
    traderGroupId: string,
    salesGroupId: string,
    diReason: DIReason,
    interventionType: CubiqTS.InterventionEventType,
  ): boolean {
    if (this.isAdmin()) {
      return true;
    }

    // DIReason CREDIT/PRICING mapping is from OMS's DIReason.java

    if (
      this.isSales() &&
      (DIReason.DAILY_REVAL_CREDIT_FAIL === diReason ||
        DIReason.ENTRY_CREDIT_FAIL === diReason ||
        DIReason.MODIFY_CREDIT_FAIL === diReason ||
        DIReason.MODIFY_CREDIT_PROXIMITY_FAIL === diReason ||
        DIReason.ENTRY_CREDIT_PROXIMITY_FAIL === diReason ||
        DIReason.FILL_CREDIT_FAIL === diReason ||
        DIReason.FORCE_TO_DI === diReason ||
        DIReason.PROXIMITY_REVAL_CREDIT_FAIL === diReason ||
        DIReason.CREDIT_REJECT_FAIL === diReason ||
        DIReason.CREDIT_REJECT_TIMEOUT === diReason ||
        DIReason.CREDIT_CANCEL_FAIL === diReason ||
        DIReason.CREDIT_CANCEL_TIMEOUT === diReason ||
        DIReason.CREDIT_EXPIRY_FAIL === diReason ||
        DIReason.CREDIT_EXPIRY_TIMEOUT === diReason ||
        CubiqTS.InterventionEventType.FWD_FILL_PENDING == interventionType)
    ) {
      if (entryUserCode == this.userCode) {
        return true;
      }

      if (salesGroupId != null) return this.isSalesGroupMember(salesGroupId);
    }

    if (
      this.isTrader() &&
      traderGroupId != null &&
      // TODO Support benchmarks
      // DIReason.SOFT_CUT_OFF == diReason ||
      // DIReason.CANCEL_DURING_SOFT_CUT_OFF == diReason ||

      (DIReason.FIXING_RATE_REQUIREMENT == diReason ||
        // check intervention type as multiple DIReasons embedded
        CubiqTS.InterventionEventType.FILL == interventionType ||
        // fwd pending
        CubiqTS.InterventionEventType.FWD_FILL_PENDING == interventionType)
      // NOT FORCE_TO_DI as it is CREDIT
    ) {
      return this.isGroupMember(traderGroupId);
    }

    return false;
  }

  public canSeeBmarkIntervention(
    orderData: CubiqTS.OrderDataImpl,
    salesGroupId: string,
    diReason: DIReason,
  ): boolean {
    if (this.isAdmin()) {
      return true;
    }
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      let entryUserCode = orderData.entryUserCode;

      // Sales User
      if (
        this.isSales() &&
        (DIReason.ENTRY_CREDIT_FAIL === diReason ||
          DIReason.MODIFY_CREDIT_FAIL === diReason ||
          DIReason.MODIFY_CREDIT_PROXIMITY_FAIL === diReason ||
          DIReason.ENTRY_CREDIT_PROXIMITY_FAIL === diReason ||
          DIReason.FORCE_TO_DI === diReason ||
          DIReason.CREDIT_EXPIRY_FAIL === diReason ||
          DIReason.CREDIT_EXPIRY_TIMEOUT === diReason ||
          DIReason.CREDIT_CANCEL_FAIL === diReason ||
          DIReason.CREDIT_CANCEL_TIMEOUT === diReason ||
          DIReason.CREDIT_REJECT_FAIL === diReason ||
          DIReason.CREDIT_REJECT_TIMEOUT === diReason ||
          DIReason.PORTFOLIO_FWD_ROLL === diReason ||
          DIReason.PORTFOLIO_FWD_ROLL_SAVED === diReason ||
          DIReason.PORTFOLIO_FWD_ROLL_AUTO_SAVED === diReason ||
          DIReason.PORTFOLIO_FWD_ROLL_ERROR === diReason)
      ) {
        //Entry User
        if (entryUserCode == this.userCode) {
          return true;
        }

        if (salesGroupId != null) {
          return this.isSalesGroupMember(salesGroupId);
        }
      }
      // Trader user
      if (
        this.isTrader() &&
        (DIReason.SOFT_CUT_OFF == diReason ||
          DIReason.CANCEL_DURING_SOFT_CUT_OFF == diReason ||
          DIReason.PORTFOLIO_FWD_ROLL == diReason ||
          DIReason.FIXING_RATE_REQUIREMENT == diReason ||
          DIReason.PORTFOLIO_FWD_ROLL_SAVED === diReason ||
          DIReason.PORTFOLIO_FWD_ROLL_AUTO_SAVED === diReason ||
          DIReason.PORTFOLIO_FWD_ROLL_ERROR === diReason)
      ) {
        //Entry User
        if (entryUserCode == this.userCode) {
          return true;
        }
        // trader group membership handled in visibility model
        //if(this.isMemberOfAnyGroup(orderData.deskId)){
        return this.visibilityModel.canSeeBmarkOrderInIntervention(
          orderData,
          this.userId,
          this.tradingGroupsIdSet,
        );
        //}
      }
    }
    return false;
  }

  public canSeeOpsInt(opsInt: CubiqTS.OpsIntEvent, record: CubiqTS.OpsExcRecordImpl): boolean {
    if (this.isAdmin()) {
      return true;
    }

    if (
      record != null &&
      CubiqTS.OpsExceptionType.AUTO_REJECTED_ORDER === record.exceptionTypeId &&
      (this.isSales() || this.isTrader())
    ) {
      // FXBFXOM-5267 4.12 relaxeed visibility for auto reject
      return true;
    }
    return this.visibilityModel.canSeeOpsInt(opsInt, this.userId);
  }

  public canSeeOrder(order: CubiqTS.OrderDataImpl, checkMonitored: boolean = true): boolean {
    if (!order) {
      return false;
    }

    if (!checkMonitored && this.isBenchmarkOrder(order)) {
      return this.canSeeBmarkOrder(order);
    }

    if (checkMonitored && !checkMonitoredOrderState(order)) {
      return false;
    }

    if (order.entryUserCode === this.userCode) {
      return true;
    }

    if (this.isAdmin()) {
      return true;
    }

    if (this.isClient()) {
      return this.canSeeClient(order.clientCode);
    }
    return this.visibilityModel.canSeeOrder(order, this.userId);
  }

  public canSeeHedgeOrder(order: CubiqTS.OrderDataImpl): boolean {
    if (this.isBenchmarkOrder(order)) {
      return this.canSeeBmarkOrder(order, false);
    }

    if (order.entryUserCode === this.userCode) {
      return true;
    }

    if (this.isAdmin()) {
      return true;
    }

    if (this.isClient()) {
      return this.canSeeClient(order.clientCode);
    }
    return this.visibilityModel.canSeeOrder(order, this.userId);
  }

  public canSeeBmarkOrder(orderData: CubiqTS.OrderDataImpl, excludeRejectedStates = true): boolean {
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      if (!this.isBenchmarkOrder(orderData)) {
        return this.canSeeOrder(orderData);
      }
      if (excludeRejectedStates && BENCHMARK_REJECT_STATES.has(orderData.orderState)) {
        return false;
      }
      if (orderData.entryUserCode === this.userCode) {
        return true;
      }
      if (this.isAdmin()) {
        return true;
      }
      if (this.isClient()) {
        return this.canSeeClient(orderData.clientCode);
      }
      return this.visibilityModel.canSeeBmarkOrder(orderData, this.userId);
    } else {
      return false;
    }
  }

  public canSeeBmarkAllocation(
    orderData: CubiqTS.OrderDataImpl,
    allocationData: CubiqTS.AllocationDataImpl,
  ): boolean {
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      allocationData instanceof CubiqTS.AllocationDataImpl
    ) {
      if (!this.isBenchmarkOrder(orderData)) {
        logger.out.error(
          `ERROR: Bmark Allocation visibility applied on non bmark order `,
          orderData,
        );
        return false;
      }
      if (BENCHMARK_REJECT_STATES.has(orderData.orderState)) {
        return false;
      }
      if (orderData.entryUserCode === this.userCode) {
        return true;
      }
      if (this.isAdmin()) {
        return true;
      }
      if (this.isClient()) {
        return this.canSeeClient(orderData.clientCode);
      }
      return this.visibilityModel.canSeeBmarkAllocation(orderData, this.userId, allocationData);
    } else {
      return false;
    }
  }

  public canSeeBmarkHedgeOrder(
    orderData: CubiqTS.OrderDataImpl,
    excludeRejectedStates = true,
  ): boolean {
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      if (!this.isBenchmarkOrder(orderData)) {
        return this.canSeeOrder(orderData);
      }
      if (excludeRejectedStates && HEDGE_ORDER_REJECT_STATES.has(orderData.orderState)) {
        return false;
      }
      if (orderData.entryUserCode === this.userCode) {
        return true;
      }
      if (this.isAdmin()) {
        return true;
      }
      if (this.isClient()) {
        return this.canSeeClient(orderData.clientCode);
      }
      return this.visibilityModel.canSeeBmarkOrder(orderData, this.userId);
    } else {
      return false;
    }
  }

  public canSeeBmarkHedgeAllocation(
    orderData: CubiqTS.OrderDataImpl,
    allocationData: CubiqTS.AllocationDataImpl,
  ): boolean {
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      allocationData instanceof CubiqTS.AllocationDataImpl
    ) {
      if (!this.isBenchmarkOrder(orderData)) {
        logger.out.error(
          `ERROR: Bmark Allocation visibility applied on non bmark order `,
          orderData,
        );
        return false;
      }
      if (HEDGE_ORDER_REJECT_STATES.has(orderData.orderState)) {
        return false;
      }
      if (orderData.entryUserCode === this.userCode) {
        return true;
      }
      if (this.isAdmin()) {
        return true;
      }
      if (this.isClient()) {
        return this.canSeeClient(orderData.clientCode);
      }
      return this.visibilityModel.canSeeBmarkAllocation(orderData, this.userId, allocationData);
    } else {
      return false;
    }
  }

  public canSeeOrderInBlotter(order: CubiqTS.OrderDataImpl): boolean {
    let algo: boolean =
      order.orderType === CubiqTS.OrderType.ALGO || order.orderType === CubiqTS.OrderType.BMALGO;
    if (
      (!algo && !ACTIVE_ORDER_STATES.has(order.orderState)) ||
      (algo && !ALGO_MONITORED_ORDER_STATES.has(order.orderState))
    ) {
      return false;
    }

    if (order.entryUserCode === this.userCode) {
      return true;
    }

    if (this.isAdmin()) {
      return true;
    }

    if (this.isClient()) {
      return this.canSeeClient(order.clientCode);
    }
    return this.visibilityModel.canSeeOrderInBlotter(order, this.userId);
  }

  public canSeeOrderInInbox(order: CubiqTS.OrderDataImpl): boolean {
    if (this.isClient()) {
      return false;
    }
    if (this.isSales()) {
      return false;
    }
    return this.visibilityModel.canSeeOrderInInbox(order, this.userId);
  }

  public canSeeClient(clientCode: string): boolean {
    if (!this.isClient()) {
      return true;
    }

    if (this.clientIdSet != null) {
      return this.clientIdSet.has(clientCode);
    }

    return false;
  }

  public isGroupMember(traderGroupId: string): boolean {
    return (
      this.tradingGroupsIdSet != null &&
      typeof traderGroupId === "string" &&
      this.tradingGroupsIdSet.has(traderGroupId)
    );
  }

  public isSalesGroupMember(salesGroupId: string): boolean {
    return this.salesGroupsIdSet != null && this.salesGroupsIdSet.has(salesGroupId);
  }

  public isBenchmarkOrder(order: CubiqTS.OrderDataImpl): boolean {
    return (
      order.orderType === CubiqTS.OrderType.BENCHMARK ||
      order.orderType === CubiqTS.OrderType.BMALGO
    );
  }

  // array based properties
  public get salesGroupsIds(): string[] {
    return Array.from(this.salesGroupsIdSet);
  }

  public set salesGroupsIds(salesGroupsIds: string[]) {
    this.salesGroupsIdSet = new Set<string>(salesGroupsIds);
  }

  public get salesGroupsNames(): string[] {
    return Array.from(this.salesGroupsNameSet);
  }

  public set salesGroupsNames(salesGroupsNames: string[]) {
    this.salesGroupsNameSet = new Set<string>(salesGroupsNames);
  }

  public get tradingGroupsIds(): string[] {
    return Array.from(this.tradingGroupsIdSet);
  }

  public set tradingGroupsIds(tradingGroupsIds: string[]) {
    this.tradingGroupsIdSet = new Set<string>(tradingGroupsIds);
  }

  public get tradingGroupsNames(): string[] {
    return Array.from(this.tradingGroupsNameSet);
  }

  public set tradingGroupsNames(tradingGroupsNames: string[]) {
    this.tradingGroupsNameSet = new Set<string>(tradingGroupsNames);
  }

  public get clientIds(): string[] {
    return Array.from(this.clientIdSet);
  }

  public set clientIds(clientIds: string[]) {
    this.clientIdSet = new Set<string>(clientIds);
  }

  public get clientNames(): string[] {
    return Array.from(this.clientNameSet);
  }

  public set clientNames(clientNames: string[]) {
    this.clientNameSet = new Set<string>(clientNames);
  }

  public get clientIdsInSalesGroups(): string[] {
    return Array.from(this.clientIdsInSalesGroupSet);
  }

  public set clientIdsInSalesGroups(ids: string[]) {
    this.clientIdsInSalesGroupSet = new Set<string>(ids);
  }
}
